// Fiche d'un véhicule : son état, ses échéances, son carnet d'entretien.
//
// Le carnet est la partie qui sert tous les jours — une ligne par passage au
// garage. Enregistrer une intervention reporte tout seul sur le véhicule le
// kilométrage du jour et l'échéance qu'elle pose : c'est le geste qu'on
// oublierait sinon.

import {
  coutsVehicule,
  echeancesVehicule,
  libelleType,
  nomVehicule,
  TYPES_INTERVENTION,
  trierInterventions,
  type UrgenceEcheance,
} from '@imp/shared/vehicules';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, ArrowLeft, Plus, Trash2 } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { formatEuro } from '@/modules/catalogue/api';
import {
  archiverVehicule,
  CARBURANTS,
  createIntervention,
  createVehicule,
  deleteIntervention,
  getVehicule,
  type InterventionInput,
  listInterventionsVehicule,
  reporterSurVehicule,
  TYPES_VEHICULE,
  updateVehicule,
  type VehiculeInput,
  versInterventionSuivi,
  versSuivi,
} from '@/modules/vehicules/api';

const URGENCE_STYLE: Record<UrgenceEcheance, string> = {
  depassee: 'bg-money-out-soft text-money-out',
  proche: 'bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-200',
  a_venir: 'bg-muted text-muted-foreground',
};

const nn = (v: string): string | null => (v.trim() === '' ? null : v.trim());
const nbOuNull = (v: string): number | null => {
  const n = Number(v.replace(/\s/g, '').replace(',', '.'));
  return v.trim() === '' || !Number.isFinite(n) ? null : Math.round(n);
};
const montant = (v: string): string => {
  const n = Number(v.replace(/\s/g, '').replace(',', '.'));
  return Number.isFinite(n) ? n.toFixed(2) : '0.00';
};
const aujourdhui = (): string => new Date().toISOString().slice(0, 10);

interface FormVehicule {
  immatriculation: string;
  type: string;
  marque: string;
  modele: string;
  annee: string;
  carburant: string;
  actif: boolean;
  kilometrage: string;
  kilometrage_releve_le: string;
  date_achat: string;
  prix_achat_ht: string;
  prochaine_ct: string;
  assurance_compagnie: string;
  assurance_expire_le: string;
  prochaine_revision_le: string;
  prochaine_revision_km: string;
  notes: string;
}

const VIDE: FormVehicule = {
  immatriculation: '',
  type: 'utilitaire',
  marque: '',
  modele: '',
  annee: '',
  carburant: '',
  actif: true,
  kilometrage: '',
  kilometrage_releve_le: '',
  date_achat: '',
  prix_achat_ht: '',
  prochaine_ct: '',
  assurance_compagnie: '',
  assurance_expire_le: '',
  prochaine_revision_le: '',
  prochaine_revision_km: '',
  notes: '',
};

function versInput(f: FormVehicule): VehiculeInput {
  return {
    immatriculation: f.immatriculation.trim().toUpperCase(),
    type: f.type,
    marque: nn(f.marque),
    modele: nn(f.modele),
    annee: nbOuNull(f.annee),
    actif: f.actif,
    kilometrage: nbOuNull(f.kilometrage),
    kilometrage_releve_le: nn(f.kilometrage_releve_le),
    carburant: nn(f.carburant),
    date_achat: nn(f.date_achat),
    prix_achat_ht: f.prix_achat_ht.trim() === '' ? null : montant(f.prix_achat_ht),
    prochaine_ct: nn(f.prochaine_ct),
    assurance_compagnie: nn(f.assurance_compagnie),
    assurance_expire_le: nn(f.assurance_expire_le),
    prochaine_revision_le: nn(f.prochaine_revision_le),
    prochaine_revision_km: nbOuNull(f.prochaine_revision_km),
    notes: nn(f.notes),
  };
}

export function VehiculeDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const nouveau = id === undefined || id === 'nouveau';

  const [form, setForm] = useState<FormVehicule>(VIDE);
  const [erreur, setErreur] = useState<string | null>(null);
  const set = (p: Partial<FormVehicule>) => {
    setForm((f) => ({ ...f, ...p }));
  };

  const vehiculeQ = useQuery({
    queryKey: ['vehicule', id],
    queryFn: () => getVehicule(id ?? ''),
    enabled: !nouveau,
  });
  const interventionsQ = useQuery({
    queryKey: ['vehicule-interventions', id],
    queryFn: () => listInterventionsVehicule(id ?? ''),
    enabled: !nouveau,
  });

  useEffect(() => {
    const v = vehiculeQ.data;
    if (!v) return;
    setForm({
      immatriculation: v.immatriculation,
      type: v.type,
      marque: v.marque ?? '',
      modele: v.modele ?? '',
      annee: v.annee === null ? '' : String(v.annee),
      carburant: v.carburant ?? '',
      actif: v.actif,
      kilometrage: v.kilometrage === null ? '' : String(v.kilometrage),
      kilometrage_releve_le: v.kilometrage_releve_le?.slice(0, 10) ?? '',
      date_achat: v.date_achat?.slice(0, 10) ?? '',
      prix_achat_ht: v.prix_achat_ht ?? '',
      prochaine_ct: v.prochaine_ct?.slice(0, 10) ?? '',
      assurance_compagnie: v.assurance_compagnie ?? '',
      assurance_expire_le: v.assurance_expire_le?.slice(0, 10) ?? '',
      prochaine_revision_le: v.prochaine_revision_le?.slice(0, 10) ?? '',
      prochaine_revision_km:
        v.prochaine_revision_km === null ? '' : String(v.prochaine_revision_km),
      notes: v.notes ?? '',
    });
  }, [vehiculeQ.data]);

  const interventions = useMemo(
    () => trierInterventions((interventionsQ.data ?? []).map(versInterventionSuivi)),
    [interventionsQ.data],
  );
  const couts = useMemo(() => coutsVehicule(interventions), [interventions]);
  const echeances = useMemo(
    () => (vehiculeQ.data ? echeancesVehicule(versSuivi(vehiculeQ.data), Date.now()) : []),
    [vehiculeQ.data],
  );

  const echec = (e: Error) => {
    setErreur(e.message);
  };
  const rafraichir = () => {
    void queryClient.invalidateQueries({ queryKey: ['vehicule', id] });
    void queryClient.invalidateQueries({ queryKey: ['vehicule-interventions'] });
    void queryClient.invalidateQueries({ queryKey: ['vehicules'] });
  };

  const enregistrerMut = useMutation({
    mutationFn: async () => {
      if (form.immatriculation.trim() === '') throw new Error("L'immatriculation est obligatoire.");
      if (nouveau) return createVehicule(versInput(form));
      await updateVehicule(id, versInput(form));
      return id;
    },
    onSuccess: (nouvelId) => {
      setErreur(null);
      rafraichir();
      if (nouveau) navigate(`/vehicules/${nouvelId}`);
    },
    onError: echec,
  });

  const archiverMut = useMutation({
    mutationFn: () => archiverVehicule(id ?? ''),
    onSuccess: () => {
      rafraichir();
      navigate('/vehicules');
    },
    onError: echec,
  });

  const titre = nouveau
    ? 'Nouveau véhicule'
    : vehiculeQ.data
      ? nomVehicule(versSuivi(vehiculeQ.data))
      : 'Véhicule';

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Button asChild variant="ghost" size="icon">
            <Link to="/vehicules" aria-label="Retour">
              <ArrowLeft className="h-4 w-4" />
            </Link>
          </Button>
          <div>
            <h1 className="text-2xl font-bold tracking-tight">{titre}</h1>
            {!nouveau && !form.actif ? (
              <span className="text-xs text-muted-foreground">Sorti du parc</span>
            ) : null}
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {!nouveau ? (
            <Button
              variant="outline"
              disabled={archiverMut.isPending}
              onClick={() => {
                archiverMut.mutate();
              }}
            >
              Sortir du parc
            </Button>
          ) : null}
          <Button
            disabled={enregistrerMut.isPending}
            onClick={() => {
              enregistrerMut.mutate();
            }}
          >
            {enregistrerMut.isPending ? 'Enregistrement…' : 'Enregistrer'}
          </Button>
        </div>
      </div>

      {erreur ? (
        <div className="rounded-md border border-destructive/30 bg-destructive/10 px-4 py-2 text-sm text-destructive">
          {erreur}
        </div>
      ) : null}

      {echeances.length > 0 ? (
        <div className="flex flex-wrap gap-2">
          {echeances.map((e) => (
            <span
              key={e.cle}
              className={`inline-flex items-center gap-1 rounded-md px-3 py-1.5 text-sm font-medium ${URGENCE_STYLE[e.urgence]}`}
            >
              {e.urgence === 'depassee' ? <AlertTriangle className="h-4 w-4" aria-hidden /> : null}
              {e.texte}
            </span>
          ))}
        </div>
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle>Le véhicule</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <Champ label="Immatriculation" id="immat">
            <Input
              id="immat"
              className="font-mono"
              placeholder="AB-123-CD"
              value={form.immatriculation}
              onChange={(e) => {
                set({ immatriculation: e.target.value });
              }}
            />
          </Champ>
          <Champ label="Type" id="type">
            <Select
              id="type"
              value={form.type}
              onChange={(e) => {
                set({ type: e.target.value });
              }}
            >
              {TYPES_VEHICULE.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </Select>
          </Champ>
          <Champ label="Carburant" id="carburant">
            <Select
              id="carburant"
              value={form.carburant}
              onChange={(e) => {
                set({ carburant: e.target.value });
              }}
            >
              {CARBURANTS.map((c) => (
                <option key={c.value} value={c.value}>
                  {c.label}
                </option>
              ))}
            </Select>
          </Champ>
          <Champ label="Marque" id="marque">
            <Input
              id="marque"
              value={form.marque}
              onChange={(e) => {
                set({ marque: e.target.value });
              }}
            />
          </Champ>
          <Champ label="Modèle" id="modele">
            <Input
              id="modele"
              value={form.modele}
              onChange={(e) => {
                set({ modele: e.target.value });
              }}
            />
          </Champ>
          <Champ label="Année" id="annee">
            <Input
              id="annee"
              inputMode="numeric"
              value={form.annee}
              onChange={(e) => {
                set({ annee: e.target.value });
              }}
            />
          </Champ>
          <Champ label="Kilométrage relevé" id="km">
            <Input
              id="km"
              inputMode="numeric"
              value={form.kilometrage}
              onChange={(e) => {
                set({ kilometrage: e.target.value });
              }}
            />
          </Champ>
          <Champ label="Relevé le" id="km_le">
            <Input
              id="km_le"
              type="date"
              value={form.kilometrage_releve_le}
              onChange={(e) => {
                set({ kilometrage_releve_le: e.target.value });
              }}
            />
          </Champ>
          <Champ label="Acheté le" id="achat">
            <Input
              id="achat"
              type="date"
              value={form.date_achat}
              onChange={(e) => {
                set({ date_achat: e.target.value });
              }}
            />
          </Champ>
          <Champ label="Prix d'achat HT (€)" id="prix">
            <Input
              id="prix"
              inputMode="decimal"
              value={form.prix_achat_ht}
              onChange={(e) => {
                set({ prix_achat_ht: e.target.value });
              }}
            />
          </Champ>
          <Champ label="Prochain contrôle technique" id="ct">
            <Input
              id="ct"
              type="date"
              value={form.prochaine_ct}
              onChange={(e) => {
                set({ prochaine_ct: e.target.value });
              }}
            />
          </Champ>
          <Champ label="Assurance — compagnie" id="assur_comp">
            <Input
              id="assur_comp"
              value={form.assurance_compagnie}
              onChange={(e) => {
                set({ assurance_compagnie: e.target.value });
              }}
            />
          </Champ>
          <Champ label="Assurance — échéance" id="assur_le">
            <Input
              id="assur_le"
              type="date"
              value={form.assurance_expire_le}
              onChange={(e) => {
                set({ assurance_expire_le: e.target.value });
              }}
            />
          </Champ>
          <Champ label="Prochaine révision — date" id="rev_le">
            <Input
              id="rev_le"
              type="date"
              value={form.prochaine_revision_le}
              onChange={(e) => {
                set({ prochaine_revision_le: e.target.value });
              }}
            />
          </Champ>
          <Champ label="Prochaine révision — km" id="rev_km">
            <Input
              id="rev_km"
              inputMode="numeric"
              value={form.prochaine_revision_km}
              onChange={(e) => {
                set({ prochaine_revision_km: e.target.value });
              }}
            />
          </Champ>
          <div className="sm:col-span-2 lg:col-span-3">
            <Label htmlFor="notes">Notes</Label>
            <Textarea
              id="notes"
              rows={2}
              value={form.notes}
              onChange={(e) => {
                set({ notes: e.target.value });
              }}
            />
          </div>
        </CardContent>
      </Card>

      {nouveau ? (
        <p className="text-sm text-muted-foreground">
          Enregistrez le véhicule pour ouvrir son carnet d&apos;entretien.
        </p>
      ) : (
        <>
          <CarnetEntretien
            vehiculeId={id}
            interventions={interventions}
            onFait={rafraichir}
            onErreur={setErreur}
          />
          {couts.nb > 0 ? (
            <Card>
              <CardHeader>
                <CardTitle>Ce que coûte ce véhicule</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 text-sm">
                <div className="flex flex-wrap gap-x-8 gap-y-1">
                  <span>
                    Total : <strong className="tabular-nums">{formatEuro(couts.totalHt)}</strong> HT
                    {' · '}
                    <span className="tabular-nums">{formatEuro(couts.totalTtc)}</span> TTC
                  </span>
                  {couts.coutParKm !== null ? (
                    <span className="text-muted-foreground">
                      Soit <strong className="tabular-nums">{formatEuro(couts.coutParKm)}</strong>{' '}
                      HT par km, sur {couts.kmCouverts?.toLocaleString('fr-FR')} km relevés
                    </span>
                  ) : null}
                </div>
                <table className="w-full">
                  <tbody>
                    {couts.parType.map((t) => (
                      <tr key={t.type} className="border-t">
                        <td className="py-1.5">{t.label}</td>
                        <td className="py-1.5 text-right text-muted-foreground">
                          {t.nb} fois
                        </td>
                        <td className="py-1.5 text-right font-medium tabular-nums">
                          {formatEuro(t.totalHt)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          ) : null}
        </>
      )}
    </div>
  );
}

function Champ({
  label,
  id,
  children,
}: {
  label: string;
  id: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id}>{label}</Label>
      {children}
    </div>
  );
}

interface FormIntervention {
  type: string;
  date_intervention: string;
  kilometrage: string;
  libelle: string;
  fournisseur: string;
  cout_ht: string;
  cout_ttc: string;
  facture_ref: string;
  prochaine_le: string;
  prochain_km: string;
  description: string;
}

const INTERVENTION_VIDE: FormIntervention = {
  type: 'entretien',
  date_intervention: aujourdhui(),
  kilometrage: '',
  libelle: '',
  fournisseur: '',
  cout_ht: '',
  cout_ttc: '',
  facture_ref: '',
  prochaine_le: '',
  prochain_km: '',
  description: '',
};

function CarnetEntretien({
  vehiculeId,
  interventions,
  onFait,
  onErreur,
}: {
  vehiculeId: string;
  interventions: ReturnType<typeof trierInterventions>;
  onFait: () => void;
  onErreur: (m: string) => void;
}) {
  const [ouvert, setOuvert] = useState(false);
  const [f, setF] = useState<FormIntervention>(INTERVENTION_VIDE);
  const set = (p: Partial<FormIntervention>) => {
    setF((v) => ({ ...v, ...p }));
  };

  const ajouterMut = useMutation({
    mutationFn: async () => {
      if (f.libelle.trim() === '') throw new Error("Décrivez l'intervention en quelques mots.");
      const input: InterventionInput = {
        vehicule_id: vehiculeId,
        type: f.type,
        date_intervention: f.date_intervention,
        kilometrage: nbOuNull(f.kilometrage),
        libelle: f.libelle.trim(),
        description: nn(f.description),
        fournisseur: nn(f.fournisseur),
        cout_ht: montant(f.cout_ht),
        cout_ttc: montant(f.cout_ttc === '' ? f.cout_ht : f.cout_ttc),
        facture_ref: nn(f.facture_ref),
        prochaine_le: nn(f.prochaine_le),
        prochain_km: nbOuNull(f.prochain_km),
      };
      await createIntervention(input);
      // Ce que l'intervention apprend au véhicule : son kilométrage du jour et
      // la prochaine échéance qu'elle pose.
      await reporterSurVehicule(vehiculeId, {
        kilometrage: input.kilometrage,
        date: input.date_intervention,
        prochaine_le: input.prochaine_le,
        prochain_km: input.prochain_km,
        type: input.type,
      });
    },
    onSuccess: () => {
      setF(INTERVENTION_VIDE);
      setOuvert(false);
      onFait();
    },
    onError: (e: Error) => {
      onErreur(e.message);
    },
  });

  const supprimerMut = useMutation({
    mutationFn: (idIntervention: string) => deleteIntervention(idIntervention),
    onSuccess: onFait,
    onError: (e: Error) => {
      onErreur(e.message);
    },
  });

  return (
    <Card>
      <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-2">
        <CardTitle>Carnet d&apos;entretien</CardTitle>
        <Button
          variant={ouvert ? 'outline' : 'default'}
          size="sm"
          onClick={() => {
            setOuvert((v) => !v);
          }}
        >
          {ouvert ? (
            'Annuler'
          ) : (
            <>
              <Plus className="h-4 w-4" /> Ajouter
            </>
          )}
        </Button>
      </CardHeader>
      <CardContent className="space-y-4">
        {ouvert ? (
          <div className="grid gap-3 rounded-md border p-3 sm:grid-cols-2 lg:grid-cols-3">
            <Champ label="Nature" id="i_type">
              <Select
                id="i_type"
                value={f.type}
                onChange={(e) => {
                  set({ type: e.target.value });
                }}
              >
                {TYPES_INTERVENTION.map((t) => (
                  <option key={t} value={t}>
                    {libelleType(t)}
                  </option>
                ))}
              </Select>
            </Champ>
            <Champ label="Date" id="i_date">
              <Input
                id="i_date"
                type="date"
                value={f.date_intervention}
                onChange={(e) => {
                  set({ date_intervention: e.target.value });
                }}
              />
            </Champ>
            <Champ label="Kilométrage" id="i_km">
              <Input
                id="i_km"
                inputMode="numeric"
                value={f.kilometrage}
                onChange={(e) => {
                  set({ kilometrage: e.target.value });
                }}
              />
            </Champ>
            <div className="space-y-1.5 sm:col-span-2 lg:col-span-3">
              <Label htmlFor="i_libelle">Intervention</Label>
              <Input
                id="i_libelle"
                placeholder="Ex : vidange + filtres, plaquettes avant"
                value={f.libelle}
                onChange={(e) => {
                  set({ libelle: e.target.value });
                }}
              />
            </div>
            <Champ label="Garage / fournisseur" id="i_four">
              <Input
                id="i_four"
                value={f.fournisseur}
                onChange={(e) => {
                  set({ fournisseur: e.target.value });
                }}
              />
            </Champ>
            <Champ label="Coût HT (€)" id="i_ht">
              <Input
                id="i_ht"
                inputMode="decimal"
                value={f.cout_ht}
                onChange={(e) => {
                  set({ cout_ht: e.target.value });
                }}
              />
            </Champ>
            <Champ label="Coût TTC (€)" id="i_ttc">
              <Input
                id="i_ttc"
                inputMode="decimal"
                placeholder="= HT si vide"
                value={f.cout_ttc}
                onChange={(e) => {
                  set({ cout_ttc: e.target.value });
                }}
              />
            </Champ>
            <Champ label="N° de facture" id="i_ref">
              <Input
                id="i_ref"
                value={f.facture_ref}
                onChange={(e) => {
                  set({ facture_ref: e.target.value });
                }}
              />
            </Champ>
            <Champ label="Prochaine échéance — date" id="i_next">
              <Input
                id="i_next"
                type="date"
                value={f.prochaine_le}
                onChange={(e) => {
                  set({ prochaine_le: e.target.value });
                }}
              />
            </Champ>
            <Champ label="Prochaine échéance — km" id="i_nextkm">
              <Input
                id="i_nextkm"
                inputMode="numeric"
                value={f.prochain_km}
                onChange={(e) => {
                  set({ prochain_km: e.target.value });
                }}
              />
            </Champ>
            <div className="sm:col-span-2 lg:col-span-3">
              <Button
                disabled={ajouterMut.isPending}
                onClick={() => {
                  ajouterMut.mutate();
                }}
              >
                {ajouterMut.isPending ? 'Enregistrement…' : "Enregistrer l'intervention"}
              </Button>
              <p className="mt-2 text-xs text-muted-foreground">
                Le kilométrage et la prochaine échéance saisis ici sont reportés sur le véhicule.
              </p>
            </div>
          </div>
        ) : null}

        {interventions.length === 0 ? (
          <p className="text-muted-foreground">
            Aucune intervention notée. Ajoutez la dernière vidange ou le dernier contrôle
            technique pour démarrer le carnet.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b text-left text-xs uppercase text-muted-foreground">
                <tr>
                  <th className="py-2 font-medium">Date</th>
                  <th className="py-2 font-medium">Nature</th>
                  <th className="py-2 font-medium">Intervention</th>
                  <th className="py-2 text-right font-medium">Km</th>
                  <th className="py-2 text-right font-medium">Coût HT</th>
                  <th className="py-2" />
                </tr>
              </thead>
              <tbody>
                {interventions.map((i) => (
                  <tr key={i.id} className="border-b last:border-0">
                    <td className="py-2 whitespace-nowrap tabular-nums">
                      {new Date(`${i.dateIntervention}T00:00:00`).toLocaleDateString('fr-FR')}
                    </td>
                    <td className="py-2">{libelleType(i.type)}</td>
                    <td className="py-2">{i.libelle}</td>
                    <td className="py-2 text-right tabular-nums text-muted-foreground">
                      {i.kilometrage === null ? '—' : i.kilometrage.toLocaleString('fr-FR')}
                    </td>
                    <td className="py-2 text-right font-medium tabular-nums">
                      {formatEuro(i.coutHt)}
                    </td>
                    <td className="py-2 text-right">
                      <Button
                        size="icon"
                        variant="ghost"
                        aria-label={`Supprimer ${i.libelle}`}
                        disabled={supprimerMut.isPending}
                        onClick={() => {
                          supprimerMut.mutate(i.id);
                        }}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
